import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input } from "@/Components/ui";
import toast from "react-hot-toast";
import { API, BACKEND_HOST } from "../../../api";
import type { DisplayMenu } from "@/api/type";

interface DisplayMenuModalProps {
    isOpen: boolean;
    toggle: () => void;
    displayMenu: DisplayMenu | null;
    onSave: (displayMenu: DisplayMenu) => void;
    siteId: string | number;
}

type DisplayMenuFormState = {
    title: string;
    description: string;
    image?: string | null;
    is_active: boolean;
};

const DisplayMenuModal: React.FC<DisplayMenuModalProps> = ({
    isOpen,
    toggle,
    displayMenu,
    onSave,
    siteId,
}) => {
    const [formData, setFormData] = useState<DisplayMenuFormState>({
        title: "",
        description: "",
        image: null,
        is_active: true,
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (displayMenu) {
            setFormData({
                title: displayMenu.title,
                description: displayMenu.description || "",
                image: displayMenu.image || null,
                is_active: displayMenu.is_active,
            });
            setImagePreview(displayMenu.image || null);
        } else {
            setFormData({
                title: "",
                description: "",
                image: null,
                is_active: true,
            });
            setImagePreview(null);
        }
        setImageFile(null);
    }, [displayMenu, isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setImageFile(file);

        if (!file) {
            setImagePreview(formData.image || null);
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const clearImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setFormData((prev) => ({ ...prev, image: null }));
    };

    const handleSave = async () => {
        if (!formData.title.trim()) {
            toast.error("메뉴 제목을 입력해주세요.");
            return;
        }

        setIsSaving(true);

        try {
            const data = new FormData();
            data.append("site", siteId.toString());
            data.append("title", formData.title.trim());
            data.append("description", formData.description);
            data.append("is_active", String(formData.is_active));

            if (imageFile) {
                data.append("image", imageFile);
            } else if (imagePreview === null) {
                data.append("image", "");
            }

            let response;
            if (displayMenu?.id) {
                response = await API.patch(`${BACKEND_HOST}/sites/display-menus/${displayMenu.id}/`, data);
            } else {
                response = await API.post(`${BACKEND_HOST}/sites/display-menus/`, data);
            }

            onSave(response.data);
            toast.success("메뉴 안내가 저장되었습니다.");
            toggle();
        } catch (error: any) {
            console.error("Save display menu error:", error);

            let errorMessage = "메뉴 안내 저장 중 오류가 발생했습니다.";
            const data = error.response?.data;
            if (data && typeof data === "object") {
                const messages = Object.keys(data).map((key) => {
                    const value = data[key];
                    return `${key}: ${Array.isArray(value) ? value.join(", ") : value}`;
                });
                if (messages.length > 0) {
                    errorMessage = messages.join("\n");
                }
            }

            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={toggle}
            size="xl"
            title={displayMenu ? "메뉴 안내 수정" : "새 메뉴 안내 등록"}
        >
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label htmlFor="display-menu-title">
                            메뉴 제목 <span className="text-danger">*</span>
                        </Label>
                        <Input
                            id="display-menu-title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="예: 시그니처 바비큐 플래터"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="display-menu-description">설명</Label>
                        <Input
                            type="textarea"
                            id="display-menu-description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="랜딩페이지에 보여줄 메뉴 설명을 입력하세요."
                            {...({ rows: 5 } as any)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="display-menu-image">대표 이미지</Label>
                        <Input
                            type="file"
                            id="display-menu-image"
                            onChange={handleImageChange}
                            accept="image/*"
                            className="rounded-lg"
                        />
                        <p className="mt-2 text-[11px] text-gray-500">
                            카드와 상세보기 상단에 노출됩니다.
                        </p>
                    </FormGroup>

                    {(imagePreview || formData.image) && (
                        <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
                            <div className="aspect-[4/3] overflow-hidden rounded-lg bg-white">
                                <img
                                    src={imagePreview || formData.image || ""}
                                    alt={formData.title || "preview"}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <div className="mt-3 flex justify-end">
                                <Button color="light" size="sm" onClick={clearImage}>
                                    이미지 제거
                                </Button>
                            </div>
                        </div>
                    )}

                    <FormGroup check>
                        <Label check className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <Input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleInputChange}
                            />
                            활성화
                        </Label>
                    </FormGroup>
                </Form>
            </ModalBody>

            <ModalFooter>
                <Button color="light" onClick={toggle}>
                    취소
                </Button>
                <Button color="dark" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? "저장 중..." : "저장"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DisplayMenuModal;
